import { FormParagraph } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IJournal } from '@common/validation/episodesAndJournal';
import type { IEpisode } from '@frontend/components/interfaces';
import type { ProfessionalSummaryDataSectionLoadResult } from '@frontend/utils/professionalSummaryDataSectionLoadResult';
import { ProfessionalSummaryDataType } from '@common/enums';
import { TableBody, TableData, TableHead, TableHeader, TableRow } from '@frontend/components/elements/common';
import { prepareAndSetJournalEntries } from '@frontend/components/pages/customControls/utils/v1/episodeJournals/episodeJournals';
import { JournalEntriesRead } from '@frontend/components/pages/professionalSummaryData/JournalEntriesRead';
import { ProfessionalSummaryDataSection } from '@frontend/components/pages/professionalSummaryData/ProfessionalSummaryDataSection';
import { ProfessionalSummaryDataTable } from '@frontend/components/pages/professionalSummaryData/ProfessionalSummaryDataTable';
import { getSelectedLanguage } from '@frontend/translate';
import { useAbortableFetch } from '@frontend/utils/hooks';

interface ProcessActivityEpisodesAndJournalsReadProps {
    processActivityId: string;
    showParent: () => void;
}
export function ProcessActivityEpisodesAndJournalsRead(props: ProcessActivityEpisodesAndJournalsReadProps) {
    const { processActivityId, showParent } = props;

    const language = getSelectedLanguage();
    const abortableFetch = useAbortableFetch();
    const [isSectionOpen, setIsSectionOpen] = useState(false);
    const [episodes, setEpisodes] = useState<IEpisode[]>([]);
    const [processActivityJournals, setprocessActivityJournals] = useState<IJournal[]>([]);
    const [episodesLoadResult, setEpisodesLoadResult] = useState<ProfessionalSummaryDataSectionLoadResult>({
        isLoadCompleted: false,
        loadFailed: false,
    });

    useEffect(() => {
        loadEpisodesAndJournal();
    }, []);

    if (episodesLoadResult.isLoadCompleted && !episodesLoadResult.loadFailed && episodes.length === 0) {
        return null;
    }

    return (
        <ProfessionalSummaryDataSection
            isOpen={isSectionOpen}
            loadResult={episodesLoadResult}
            onToggle={() => setIsSectionOpen(!isSectionOpen)}
            professionalSummaryDataType={ProfessionalSummaryDataType.episodesAndJournals}
            readOnly
            selectionCount={episodes.length}
        >
            {episodes && episodes.length > 0 && (
                <ProfessionalSummaryDataTable isRead={true}>
                    <TableHead>
                        <TableRow>
                            <TableHeader>{language.episodes.displayName}</TableHeader>
                            <TableHeader>{language.episodes.description}</TableHeader>
                            <TableHeader>{language.episodes.icpc}</TableHeader>
                            <TableHeader>{language.common.startDate}</TableHeader>
                            <TableHeader>{language.common.endDate}</TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {episodes.map((episode) => {
                            return (
                                <TableRow key={episode.id}>
                                    <TableData>{episode.valueDisplayName}</TableData>
                                    <TableData>{episode.comment}</TableData>
                                    <TableData style={{ whiteSpace: 'nowrap' }}>{episode.statusCode}</TableData>
                                    <TableData style={{ whiteSpace: 'nowrap' }}>
                                        <FormParagraph>{episode.startDate}</FormParagraph>
                                    </TableData>
                                    <TableData style={{ whiteSpace: 'nowrap' }}>
                                        <FormParagraph>{episode.endDate}</FormParagraph>
                                    </TableData>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </ProfessionalSummaryDataTable>
            )}

            {episodes && processActivityJournals ? (
                <JournalEntriesRead journalEntries={processActivityJournals} />
            ) : null}
        </ProfessionalSummaryDataSection>
    );

    async function loadEpisodesAndJournal() {
        Promise.all([
            abortableFetch.fetchJson(`/api/processActivities/${processActivityId}/episodes`, 'GET'),
            abortableFetch.fetchJson(`/api/processActivities/${processActivityId}/journalEntries`, 'GET'),
        ])
            .then(([processActivityEpisodesResponse, processActivityJournalEntriesResponse]) => {
                setEpisodesLoadResult({ isLoadCompleted: true, loadFailed: false });

                if (
                    processActivityEpisodesResponse.json.total > 0 ||
                    processActivityJournalEntriesResponse.json.total > 0
                ) {
                    showParent();
                }

                if (processActivityEpisodesResponse.json.items) {
                    setEpisodes(processActivityEpisodesResponse.json.items);
                }

                if (processActivityJournalEntriesResponse.json.items?.length > 0) {
                    const journals: IJournal[] = prepareAndSetJournalEntries(
                        processActivityJournalEntriesResponse.json.items,
                        processActivityEpisodesResponse.json.items
                    ).toArray();

                    setprocessActivityJournals(journals);
                }
            })
            .catch(() => {
                setEpisodesLoadResult({ isLoadCompleted: true, loadFailed: true });
            });
    }
}
